import { useCallback, useState } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { ASSETS_BASE } from '../assets/manifest.js';
// A lista de abas mora junto com o endereço que as nomeia: são a mesma coisa
// dita duas vezes, e duas listas separadas divergem no primeiro acréscimo.
import type { AppTab } from '../nav/route.js';

export type { AppTab };

export function HeaderNav({
  activeTab,
  onTabChange,
  onOpenAuth,
}: {
  readonly activeTab: AppTab;
  readonly onTabChange: (tab: AppTab) => void;
  readonly onOpenAuth: () => void;
}): JSX.Element {
  const { user, stats, loading, logout } = useAuth();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('oz_nav_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('oz_nav_collapsed', String(next));
      } catch {
        // Ignora erro de storage
      }
      return next;
    });
  }, []);

  const getPortraitUrl = (portraitName: string) => {
    return `${ASSETS_BASE}/img/faces/${portraitName}.webp`;
  };

  /**
   * A aba aparece para qualquer conta autenticada, e não só para admins.
   *
   * Escondê-la de quem não tem o papel fazia o painel — com os editores de
   * classes, personagens, armas e inimigos — simplesmente sumir da tela, sem
   * dizer por quê: uma sessão vencida e o editor "não existia mais". Quem
   * entra e não tem permissão agora vê a aba e a explicação lá dentro. Quem
   * manda de verdade continua sendo o servidor, que responde 403 a qualquer
   * escrita vinda de conta sem papel.
   */
  const showAdminTab = user !== null;

  return (
    <div className={`main-nav-wrapper ${collapsed ? 'collapsed' : ''}`}>
      <header className="main-nav-bar">
      <div className="nav-brand" onClick={() => onTabChange('skirmish')}>
        <span className="brand-icon">⚔️</span>
        <div className="brand-info">
          <span className="brand-title">OZ EMBLEM</span>
          <span className="brand-stage">Etapa 8 · História & Online</span>
        </div>
      </div>

      <nav className="nav-tabs" aria-label="Navegação principal">
        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'skirmish' ? 'active' : ''}`}
          onClick={() => onTabChange('skirmish')}
        >
          ⚔️ Escaramuça
        </button>
        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'campaign' ? 'active' : ''}`}
          onClick={() => onTabChange('campaign')}
        >
          📖 História
        </button>
        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'online' ? 'active' : ''}`}
          onClick={() => onTabChange('online')}
        >
          🌐 Online
        </button>
        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'shop' ? 'active' : ''}`}
          onClick={() => onTabChange('shop')}
        >
          🛒 Loja
        </button>
        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'community' ? 'active' : ''}`}
          onClick={() => onTabChange('community')}
        >
          👤 Comunidade
        </button>
        {user !== null && (
          <button
            type="button"
            className={`nav-tab-btn ${activeTab === 'maps' ? 'active' : ''}`}
            onClick={() => onTabChange('maps')}
          >
            🗺️ Mapas
          </button>
        )}
        {user !== null && (
          <button
            type="button"
            className={`nav-tab-btn ${activeTab === 'assets' ? 'active' : ''}`}
            onClick={() => onTabChange('assets')}
          >
            🎨 Acervo
          </button>
        )}
        {showAdminTab && (
          <button
            type="button"
            className={`nav-tab-btn nav-tab-admin ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => onTabChange('admin')}
          >
            ⚙️ Painel Admin
          </button>
        )}
      </nav>

      <div className="nav-user-area">
        {loading ? (
          <span className="muted">carregando…</span>
        ) : user ? (
          <div className="user-logged-box">
            <div
              className="user-avatar-mini"
              style={{
                backgroundImage: `url(${getPortraitUrl(user.avatarPortrait)})`,
                backgroundSize: '400% 200%',
                backgroundPosition: '0 0',
              }}
              title={user.username}
            />
            <div className="user-details-mini">
              <div className="user-name-line">
                <span className="user-name">{user.username}</span>
                <span className={`user-role-badge ${user.role}`}>{user.role.toUpperCase()}</span>
              </div>
              <div className="user-quick-stats">
                <span>🏆 {stats?.skirmishesWon ?? 0}V</span>
                <span className="muted">({stats?.winRate ?? 0}%)</span>
                <span>⭐ {stats?.eloRating ?? 1000}</span>
              </div>
            </div>
            <button
              type="button"
              className="user-profile-btn"
              onClick={() => onTabChange('community')}
              title="Acessar Perfil"
            >
              Perfil
            </button>
            <button
              type="button"
              className="user-logout-btn"
              onClick={() => void logout()}
              title="Encerrar Sessão"
            >
              Sair
            </button>
          </div>
        ) : (
          <button type="button" className="auth-open-btn" onClick={onOpenAuth}>
            🔑 Entrar / Cadastrar
          </button>
        )}
        <button
          type="button"
          className="nav-collapse-btn"
          onClick={toggleCollapsed}
          title="Ocultar menu superior para tela cheia / foco no jogo"
          aria-label="Ocultar menu superior"
        >
          ▲ Ocultar
        </button>
      </div>
    </header>
    {collapsed && (
      <button
        type="button"
        className="nav-expand-floating-pill"
        onClick={toggleCollapsed}
        title="Expandir menu de navegação"
        aria-label="Expandir menu de navegação"
      >
        <span className="pill-icon">⚔️</span>
        <span className="pill-label">MENU</span>
        <span className="pill-arrow">▼</span>
      </button>
    )}
  </div>
  );
}

